import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/providers';

interface ProtectedRouteProps {
    children: ReactNode;
    requireAuth?: boolean;
    redirectTo?: string;
}

/**
 * ProtectedRoute component handles authentication-based route protection
 * 
 * @param children - The component(s) to render if access is allowed
 * @param requireAuth - Whether authentication is required (default: true)
 * @param redirectTo - Where to redirect if access is denied
 */
export function ProtectedRoute({
    children,
    requireAuth = true,
    redirectTo
}: ProtectedRouteProps) {
    const { isAuthenticated, isLoading } = useAuth();
    const location = useLocation();

    // Show loading state while checking authentication
    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    // If route requires auth but user is not authenticated
    if (requireAuth && !isAuthenticated) {
        // Redirect to login and save the attempted location
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // If route is for non-authenticated users but user is authenticated
    if (!requireAuth && isAuthenticated) {
        // Check if there's a saved location to redirect to, otherwise go to app
        const from = location.state?.from?.pathname || '/app';
        return <Navigate to={redirectTo || from} replace />;
    }

    // Access is allowed, render the children
    return <>{children}</>;
}

/**
 * PublicRoute - For routes that should only be accessible when NOT authenticated
 * (login, register, landing page)
 */
export function PublicRoute({ children, redirectTo = '/app' }: {
    children: ReactNode;
    redirectTo?: string;
}) {
    return (
        <ProtectedRoute requireAuth={false} redirectTo={redirectTo}>
            {children}
        </ProtectedRoute>
    );
}

/**
 * PrivateRoute - For routes that require authentication
 */
export function PrivateRoute({ children }: { children: ReactNode }) {
    return (
        <ProtectedRoute requireAuth={true}>
            {children}
        </ProtectedRoute>
    );
}
